import { FormEvent, useState } from "react";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import useFiles, { useFilesSearch } from "files/hooks/useFiles";
import { StringParam, useQueryParam } from "use-query-params";
import TextOverflow from "~/components/TextOverflow";
import useToast from "~/components/ui/toast";

interface ModalProps {
  open: boolean;
  handleClose: (deleted: boolean) => void;
  isDirectory: boolean;
  fileKey: string;
  /**
   * Explicit directory prefix to revalidate after the delete completes. Pass this for
   * callers (like PersonalDatasetFiles) that track the browsed folder as local state
   * rather than the URL, since `useFiles()` otherwise resolves the current directory
   * from the route.
   */
  prefix?: string;
}

const ModalBody = ({ handleClose, isDirectory, fileKey, prefix }: ModalProps) => {
  const [search] = useQueryParam("q", StringParam);
  const { mutate: mutateFiles } = useFiles(prefix);
  const { mutate: mutateFilesSearch } = useFilesSearch();
  const { showSuccess, showError } = useToast();
  const filename = fileKey.split("/").at(isDirectory ? -2 : -1);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const { VITE_RWB_API } = import.meta.env;
    const resp = await fetch(`${VITE_RWB_API}/file-browser/${encodeURIComponent(fileKey)}`, {
      method: "DELETE",
    });

    if (resp.ok) {
      if (search) {
        await mutateFilesSearch();
      } else {
        await mutateFiles();
      }

      showSuccess(`Deleted "${filename}"`);
      handleClose(true);
    } else {
      setError("Unable to delete file");
      showError(`Unable to delete "${filename}"`);
    }

    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <DialogTitle>
        <TextOverflow>Delete &quot;{filename}&quot;</TextOverflow>
      </DialogTitle>
      <DialogContent sx={{ gap: 2, fontSize: 14 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          Are you sure you want to delete this{" "}
          {isDirectory ? (
            <>
              folder and <b>all of its contents</b>
            </>
          ) : (
            "file"
          )}
          ?
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => handleClose(false)}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          loading={submitting}
          sx={{
            backgroundColor: "attention.main",
            "&:hover": { backgroundColor: "attention.dark" },
          }}
        >
          Delete
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

const DeleteFileDialog = (props: ModalProps) => (
  <Dialog fullWidth open={props.open} onClose={props.handleClose}>
    <ModalBody {...props} />
  </Dialog>
);

export default DeleteFileDialog;
